import React from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';
import logger from '../utils/logger';

/**
 * App-wide error boundary. If any lazy-loaded chunk fails to load or any
 * descendant component throws, show a friendly recovery card instead of
 * a blank white page. Two recovery paths:
 *   1. Hard reload (bypasses service-worker cache, fixes most stale-chunk bugs).
 *   2. Clear all app state and go home.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface the error in devtools for easier remote debugging.
    logger.error('App error boundary caught:', error, info);
  }

  handleReload = () => {
    // Unregister any stale service workers + wipe caches, then hard-reload.
    // This fixes "I see a blank page" caused by an outdated PWA cache.
    const purge = async () => {
      try {
        if ('serviceWorker' in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
      } catch (err) {
        logger.warn('Cache purge failed:', err);
      } finally {
        window.location.reload();
      }
    };
    purge();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Something went wrong loading this page.';
    const looksLikeChunkError = /chunk|loading|failed|network/i.test(msg);

    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050508] p-6">
        <div className="max-w-md w-full bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
          <h1 className="text-xl font-semibold text-white mb-2">
            {looksLikeChunkError ? 'Refresh needed' : 'Something went wrong'}
          </h1>
          <p className="text-sm text-white/70 mb-6">
            {looksLikeChunkError
              ? 'We just released an update. A quick refresh will load the latest version.'
              : msg}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-white rounded-lg text-sm font-medium"
              data-testid="error-boundary-reload"
            >
              <RotateCcw className="w-4 h-4" /> Refresh app
            </button>
            <button
              onClick={this.handleGoHome}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium"
              data-testid="error-boundary-home"
            >
              <Home className="w-4 h-4" /> Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
